/**
 * Import guest data dialog.
 *
 * Shows when a user with existing guest data logs in for the first time.
 * Offers to import their guest progress to their new account.
 */

import { Upload, Trash2, Loader2 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useStores } from '@/stores/StoreContext';

interface ImportGuestDataDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog should close */
  onClose: () => void;
}

/**
 * Dialog for importing guest data to authenticated account.
 */
export const ImportGuestDataDialog = observer(function ImportGuestDataDialog({
  open,
  onClose,
}: ImportGuestDataDialogProps): React.ReactElement {
  const { authStore } = useStores();
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guestData = authStore.guestData;
  const createdDate = guestData ? new Date(guestData.createdAt).toLocaleDateString() : null;

  const handleImport = async (): Promise<void> => {
    setImporting(true);
    setError(null);

    const result = await authStore.importGuestData();

    if (result.success) {
      onClose();
    } else {
      setError(result.error ?? 'Failed to import data');
    }

    setImporting(false);
  };

  const handleSkip = (): void => {
    // Clear guest data without importing
    if (guestData) {
      // Just close - guest data will be overwritten by authenticated data
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import Your Progress?</DialogTitle>
          <DialogDescription>
            We found progress from when you played as a guest. Would you like to import it to your
            account?
          </DialogDescription>
        </DialogHeader>

        {guestData && (
          <div className="rounded-lg border bg-muted/50 p-4">
            <h4 className="text-sm font-medium">Guest Progress</h4>
            <p className="mt-1 text-xs text-muted-foreground">Started: {createdDate}</p>
            {/* Future: Show character level, playtime, etc. */}
            <p className="mt-2 text-sm text-muted-foreground">
              Preferences and settings will be transferred.
            </p>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleSkip}
            disabled={importing}
            className="w-full sm:w-auto"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Start Fresh
          </Button>
          <Button
            onClick={() => void handleImport()}
            disabled={importing}
            className="w-full sm:w-auto"
          >
            {importing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Import Progress
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
